import EVENTS from "core/customEvent/events.js";
import _ from "lodash";

import { globalImageIdSpecificToolStateManager } from "./imageIdSpecificStateManager.js";
import triggerEvent from "core/customEvent/triggerEvent.js";
import {
  getEnabledElement,
  getEnabledElements,
  clearAllImage,
} from "component/toolManager/enabledElements.js";

function getElementToolStateManager(element) {
  const enabledImage = element;
  // If the enabledImage has no toolStateManager, create a default one for it
  // NOTE: This makes state management element specific

  if (enabledImage.toolStateManager === undefined) {
    enabledImage.toolStateManager = globalImageIdSpecificToolStateManager;
  }

  return enabledImage.toolStateManager;
}

/**
 * Sets the tool state manager for an element
 * @public
 * @method setElementToolStateManager
 *
 * @param  {HTMLElement} element The element.
 * @param {Object} toolStateManager The toolStateManager.
 * @returns {undefined}
 */
function setElementToolStateManager(element, toolStateManager) {
  const enabledElement = getEnabledElement(element);

  enabledElement.toolStateManager = toolStateManager;
}

function clearElementState(element) {
  const toolStateManager = getElementToolStateManager(element);
  toolStateManager.clearView(element);
}

function clearToolState(element, toolType) {
  const toolStateManager = getElementToolStateManager(element);
  const toolData = toolStateManager.get(element, toolType);

  // If any toolData actually exists, clear it
  if (toolData !== undefined) {
    toolData.data = [];
  }
}

function clearAll() {
  let enabledElements = getEnabledElements();
  enabledElements.forEach(function (enabledElement) {
    const toolStateManager = getElementToolStateManager(enabledElement.element);
    toolStateManager.clearAll(enabledElement.element);
  });
  clearAllImage();
}

/**
 * Adds tool state to the toolStateManager, this is done by tools as well
 * as modules that restore saved state.
 * @public
 * @method addToolState
 *
 * @param  {HTMLElement} element  The element.
 * @param  {string} toolType      The toolType of the state.
 * @param  {Object} measurementData The data to store in the state.
 * @returns {undefined}
 */
function addToolState(element, toolType, measurementData) {
  const toolStateManager = getElementToolStateManager(element);

  toolStateManager.add(element, toolType, measurementData);

  const eventType = EVENTS.MEASUREMENT_ADDED;
  const eventData = {
    toolType,
    element,
    measurementData,
  };

  triggerEvent(element, eventType, eventData);
}
/**
 * Returns tool specific state of an element. Used by tools as well as modules
 * that save state persistently
 * @export
 * @public
 * @method
 * @name getToolState
 *
 * @param  {HTMLElement} element The element.
 * @param  {string} toolType The toolType of the state.
 * @returns {Object}          The element's state for the given toolType.
 */
function getToolState(element, toolType) {
  const toolStateManager = getElementToolStateManager(element);

  return toolStateManager.get(element, toolType);
}

function invalidateToolStateByEnabledElement(element, toolType) {
  //const element = enabledElement.element;
  // if (!_.get(toolData, "data")) {
  //   return;
  // }

  let toolData = getToolState(element, toolType);
  const maxSegmentations = 1;

  for (let segIndex = 0; segIndex < maxSegmentations; segIndex++) {
    if (!_.get(toolData, "data")) {
      // No data, no render.
      continue;
    }

    if (!toolData.data[segIndex] || !toolData.data[segIndex].pixelData) {
      // No data, no render.
      continue;
    }
    toolData.data[segIndex].invalidated = true;
  }
}

/**
 * Removes specific tool state from the toolStateManager.
 * @public
 * @method removeToolState
 *
 * @param  {HTMLElement} element  The element.
 * @param  {string} toolType      The toolType of the state.
 * @param  {Object} data          The data to remove from the toolStateManager.
 * @returns {undefined}
 */
function removeToolState(element, toolType, data) {
  const toolStateManager = getElementToolStateManager(element);
  const toolData = toolStateManager.get(element, toolType);
  // Find this tool data
  let indexOfData = -1;

  for (let i = 0; i < toolData.data.length; i++) {
    if (toolData.data[i] === data) {
      indexOfData = i;
    }
  }

  if (indexOfData !== -1) {
    toolData.data.splice(indexOfData, 1);

    const eventType = EVENTS.MEASUREMENT_REMOVED;
    const eventData = {
      toolType,
      element,
      measurementData: data,
    };

    triggerEvent(element, eventType, eventData);
  }
}

export {
  addToolState,
  getToolState,
  removeToolState,
  clearElementState,
  clearToolState,
  clearAll,
  setElementToolStateManager,
  getElementToolStateManager,
  invalidateToolStateByEnabledElement,
};
